import {
    setDateReviver,
    setLoginRedirect,
    setPortalResolver,
    setVersionHandler,
} from "@btnet/services-btnet/apix-handler";

import { convertServerDateStringToDate } from "utilities/api.utilities";
import { getCurrentPortalType, handleRedirectToLogin } from "utilities/portal/portal";
import { checkForNewAppVersion } from "utilities/Versioning/versionWatcher";

/**
 * Injects the Clients.App browser behaviors into the `@btnet/services-btnet`
 * mutator via its DI setters. The package mutator ships Node/Playwright-safe
 * inline defaults; here we override them with the app's real implementations so
 * package-imported endpoints behave identically to ones still on the app mutator:
 *
 * - portal resolver -> `getCurrentPortalType` (share-token / portal-aware; the
 *   package default only inspects the URL). Stringified because the package
 *   setter wants `() => string` and the app enum is numeric — `.toString()`
 *   yields the same numeric `PortalType` header the package default produces.
 * - login redirect -> `handleRedirectToLogin` (Entra-aware 401 routing to
 *   `/apix/v2/admin/auth/login`; the package default hardcodes `/api/Admin/Login`).
 * - version handler -> `checkForNewAppVersion` (writes via `BTLocalStorage`; the
 *   package default writes raw `localStorage`).
 * - date reviver -> `convertServerDateStringToDate` (app's canonical parser).
 *
 * Imported for its side effect from `index-app.tsx` at app boot, before any
 * APIX call (mirrors `utilities/webview/setupMobileMock`). The app mutator
 * (`utilities/api/apix-handler.ts`) is left untouched and remains the default
 * for handlers still imported from the app's own generated tree.
 *
 * Exported so tests can drive it directly; the module-level call below is the
 * single production caller.
 */
export function setupApix(): void {
    // getCurrentPortalType is deprecated in favor of the UserInfoContext hook, but a
    // React hook can't run at the mutator boundary; the app mutator
    // (utilities/api/apix-handler.ts) resolves the PortalType header the same way.
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    setPortalResolver(() => getCurrentPortalType().toString());
    setLoginRedirect(handleRedirectToLogin);
    setVersionHandler(checkForNewAppVersion);
    setDateReviver(convertServerDateStringToDate);
}

setupApix();
